import { useMemo } from 'react';
import { Object3D, Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { PlanProjectionGroup } from 'app/modules/wallforming/components/scenes/objects/dimensions/PlanProjectionGroup';
import { getCornerRotationDir } from 'app/modules/wallforming/components/scenes/objects/dimensions/utils/dimensionUtils';
import {
    isCornerSingleSidePoi,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const PlanProjection = () => {
    const { selectedPoi, selectedPoiIndex } = usePlacementStore(
        useShallow((state) => ({
            selectedPoi:
                state.poiSelection.current.index !== undefined
                    ? (state.pois[state.poiSelection.current.index]?.poi ?? null)
                    : null,
            selectedPoiIndex: state.poiSelection.current.index
        }))
    );

    const dir = useMemo(() => {
        if (!selectedPoi) {
            return null;
        }

        if (isCornerSingleSidePoi(selectedPoi)) {
            const { topPoint, bottomPoint } = selectedPoi;
            return new Vector3().copy(topPoint).sub(bottomPoint).normalize();
        }
        if (!isPlanarFacePoi(selectedPoi)) {
            return null;
        }

        return new Vector3().crossVectors(selectedPoi.formFace.normal, Object3D.DEFAULT_UP).normalize();
    }, [selectedPoi]);

    const rotationReferenceDir = useMemo(() => {
        if (!selectedPoi) {
            return null;
        }

        if (isCornerSingleSidePoi(selectedPoi)) {
            const { topPoint, bottomPoint } = selectedPoi;
            return new Vector3().copy(topPoint).sub(bottomPoint).normalize();
        }
        if (!isPlanarFacePoi(selectedPoi)) {
            return null;
        }

        return new Vector3(
            selectedPoi.formFace.normal.x,
            selectedPoi.formFace.normal.y,
            selectedPoi.formFace.normal.z
        ).normalize();
    }, [selectedPoi]);

    const mid = useMemo(() => {
        if (!selectedPoi || selectedPoiIndex === undefined) {
            return null;
        }

        if (isPlanarFacePoi(selectedPoi)) {
            const points = selectedPoi.formFace.outerWire.edges.flatMap((edge) => [edge.startPoint, edge.endPoint]);
            if (points.length < 2) {
                return null;
            }

            const [p1, p2] = [...points]
                .sort((a, b) => a.z - b.z)
                .slice(0, 2)
                .map((p) => new Vector3(p.x, p.y, p.z));

            return p1.clone().add(p2).multiplyScalar(0.5);
        }

        if (!isCornerSingleSidePoi(selectedPoi)) {
            return null;
        }

        return new Vector3(selectedPoi.bottomPoint.x, selectedPoi.bottomPoint.y, selectedPoi.bottomPoint.z);
    }, [selectedPoi, selectedPoiIndex]);

    const rotationDir = useMemo(() => {
        if (!selectedPoi || !isCornerSingleSidePoi(selectedPoi)) {
            return null;
        }

        return getCornerRotationDir(selectedPoi);
    }, [selectedPoi]);

    if (!mid || !dir || selectedPoiIndex === undefined) {
        return null;
    }

    return (
        <PlanProjectionGroup
            dir={dir}
            mid={mid}
            rotationDir={rotationDir ?? undefined}
            rotationReferenceDir={rotationReferenceDir ?? undefined}
            selectedPoiIndex={selectedPoiIndex}
        />
    );
};
